import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, combineLatest, forkJoin, iif, of, throwError } from 'rxjs';
import { catchError, map, concatMap } from 'rxjs/operators';
import moment from "moment";
import { environment } from '@env/environment';
import {
  HTMLReport,
  ContentHeaderBlock,
  RelatedContent,
  Section,
  SectionContent,
  HygraphImage,
  PlatformImage,
  AssetDetails
} from '@shared/services/hygraph/hygraph.models';
import { CategoryTag } from '@app/@shared/models/search/cti-search.model';
import { ThumbnailsService } from '../thumbnails/thumbnails.service';
import { Router } from '@angular/router';
import { MarketAnalysisService } from '../market-analysis/market-analysis.service';

const headers = new HttpHeaders().set('X-API-Version', 'v1');

@Injectable({
  providedIn: 'root'
})
export class HygraphService {
  private readonly THUMBNAIL_HEIGHT = 800;
  private readonly THUMBANAIL_WIDTH = 1200;

  constructor(
    private readonly http: HttpClient,
    private readonly thumbnailService: ThumbnailsService,
    private readonly marketAnalysisService: MarketAnalysisService,
    private readonly router: Router
  ) { }

  /**
   * Gets report for asset id
   *
   * @param id asset id
   * @param analysisCode optional analysisCode for entitled flag
   * @returns HTMLReport
   */
  public getReport(
    id: string,
    analysisCode?: string,
    stage?: string
  ): Observable<HTMLReport> {
    const url = `${environment.metaServiceBaseUrl}/contents/${id}`.concat(stage ? `?stage=${stage}` : '');

    return combineLatest([
      this.http.get<AssetContentResponse>(url, { headers }),
      iif(() => !!analysisCode,
        this.marketAnalysisService.getAnalysisMetadata(analysisCode).pipe(map(({ report }) => report.entitled)),
        of(true))
    ]).pipe(
      concatMap(([response, entitled]) => {
        return this.buildHTMLReportFromResponse(response).pipe(
          map(htmlReport => ({
            ...htmlReport,
            assetDetails: response.assetDetails,
            downloadEnabled: response.downloadEntitled,
            entitled
          }))
        );
      })
    );
  }

  /**
   * Gets a Hygraph blog like html report
   * @param id blog/asset id
   * @param stage optional stage
   * @returns HTMLReport
   */
  public getBlog(id: string, stage?: string): Observable<HTMLReport> {
    let url = `${environment.metaServiceBaseUrl}/contents/${id}`;
    url = url.concat(stage ? `?stage=${stage}` : '');

    return this.http.get<ContentsResponse>(url, { headers }).pipe(
      concatMap(response => this.buildHTMLReportFromResponse(response))
    );
  }

  /**
   * Gets a blog from the free api
   * @param hygraphId asset id
   * @returns HtmlReport plus trackviews
   */
  public getContentIdFromHygraphId(hygraphId: string): Observable<string> {
    const url = `${environment.metaServiceBaseUrl}/contents/?stage=PREVIEW&hygraphId=${hygraphId}`;

    return this.http.get<ContentsResponse[]>(url, { headers }).pipe(
      map(res => res[0].id)
    );
  }

  public getContentIdFromAssetId(assetId: string): Observable<string> {
    const url = `${environment.metaServiceBaseUrl}/asset_content/${assetId}`;

    return this.http.get<AssetContentResponse>(url, { headers }).pipe(
      map(res => res.contentId)
    );
  }

  /**
   * Gets a blog from the free api
   * @param id asset id
   * @returns HtmlReport plus trackviews
   */
  public getFreeBlog(id: string): Observable<HTMLReport & { trackViews: boolean, showBanner: boolean }> {
    const url = `${environment.publicServiceBaseUrl}/v1/contents`;

    return this.http.get<ContentsResponse>(url, { params: { id }, headers }).pipe(
      concatMap(response => this.buildHTMLReportFromResponse(response).pipe(
        map(htmlReport => ({
          ...htmlReport,
          trackViews: response.content.trackViews ?? true,
          showBanner: response.content.showBanner ?? true
        }))
      )),

    )
  }

  /**
   * Gets a report from the free api
   * it will eventually need to be deprecated
   * since asset id wont be supported in the future
   * @param id asset id
   * @param segment optional segment
   * @returns htlm report plus trackViews
   */
  public getFreeAssetReport(
    id: string, segment?: string
  ): Observable<HTMLReport & { trackViews: boolean, showBanner: boolean }> {
    const url = `${environment.publicServiceBaseUrl}/v1/asset_content`;
    const params = segment ? { id, segment } : { id };

    return this.http.get<ContentsResponse>(url, { headers, params }).pipe(
      concatMap(response => this.buildHTMLReportFromResponse(response).pipe(
        map(htmlReport => ({
          ...htmlReport,
          trackViews: response.content.trackViews ?? true,
          showBanner: response.content.showBanner ?? true
        }))
      )),
    )
  }

  /**
   * Gets a report from the free api
   * @param id asset id
   * @param segment optional segment
   * @returns htlm report plus trackViews
   */
  public getFreeReport(
    id: string, segment?: string
  ): Observable<HTMLReport & { trackViews: boolean, showBanner: boolean }> {
    const url = `${environment.publicServiceBaseUrl}/v1/contents`;
    const params = segment ? { id, segment } : { id };

    return this.http.get<ContentsResponse>(url, { headers, params }).pipe(
      concatMap(response => this.buildHTMLReportFromResponse(response).pipe(
        map(htmlReport => ({
          ...htmlReport,
          trackViews: response.content.trackViews ?? true,
          showBanner: response.content.showBanner ?? true
        }))
      )),
    )
  }

  /**
   * Gets report item information
   *
   * @param id The parameter could be: <hygraph_object_id|cl_object_id>
   */
  public getReportItem(id: string): Observable<HTMLReport> {
    const url = `${environment.reportService}/reports/inside-tech/${id}`;
    headers.set('Accept', 'application/json');

    return this.http.get<any>(url, { headers }).pipe(
      concatMap(response => {
        return this.getSections(response.contentBodyBlock?.section ?? [])
          .pipe(map(
            sections => ({
              publishDate: new Date(response.publishDate),
              header: this.getHeader(response),
              sections: sections,
              name: response.name,
              relatedContent: this.getRelatedContent(response.relatedContent),
              includeRelatedContent: response.includeRelatedContent,
              includeTableOfContent: response.includeTableOfContent,
              reportCode: response.reportCode,
              reportTitle: response.reportTitle,
            })
          ))
      }),
      catchError((error: any) => {
        return throwError(error);
      })
    );
  }

  /**
   * Given a backend response hygraph like it is mapped to a standarized form for the platform
   * @param response to be processed
   * @returns HTMLReport
   */
  private buildHTMLReportFromResponse(response: AssetContentResponse | ContentsResponse): Observable<HTMLReport> {
    return this.getSections(response.content.contentBodyBlock?.section || [])
      .pipe(map(
        sections => ({
          publishDate: moment.utc(response.content.publishDate, true).toDate(),
          header: this.getHeader(response.content),
          sections: sections,
          name: response.name,
          relatedContent: this.getRelatedContent(response.content.relatedContent),
          includeRelatedContent: response.content.includeRelatedContent,
          includeTableOfContent: response.content.includeTableOfContent,
          reportCode: response.reportCode,
          reportTitle: response.reportTitle,
        })
      ));
  }

  private getRelatedContent(relatedContent: any[]): RelatedContent[] {
    return relatedContent.map(data => ({
      title: (data.linkTitle || data.blogTitle || data.reportTitle || data.QuickSightDashboard_title) as string,
      relatedContentType: data.relatedContentType?.toLowerCase() ?? data.contentType?.toLowerCase(),
      publishDate: data.publishDate ?? data.blogPublishDate,
      contentLink: this.generateRelatedContentLink(data.contentType?.toLowerCase(), data)
    }));
  }

  private getHeader(data: Report) {

    const allTags = [
      ...data.categoryTags ?? [],
      ...(
        data.ignoreDefaultTags ?
          []
          : (data.categoryTagDefaults ?? [])
      )
    ];


    const mappedTags = this.mapHygraphCategoryTags(allTags);

    const contentHeaderBlock: ContentHeaderBlock = {
      id: data.id,
      title: data.title,
      subTitle: data.subTitle,
      summary: data.summary,
      authors: data.authors,
      customerSegment: data.customerSegment,
      technologyVertical: data.technologyVertical,
      themes: data.themes,
      featuredImage: data.featuredImage,
      contentType: data.contentType,
      categoryTags: mappedTags,
    }
    return contentHeaderBlock;
  }

  private getSections(sections: Section[]): Observable<Section[]> {
    return forkJoin(
      sections.map(section => {
        // Get SectionContent for the actual section
        const sectionContent$ = section.sectionContent && section.sectionContent.length !== 0
          ? this.mapSectionImages(section)
          : of(section);
        // Recursively map subsections if they exist
        const subsections$ = section.subsections && section.subsections.length !== 0
          ? this.getSections(section.subsections)
          : of([]);
        // Combine the section content and subsections
        return forkJoin([sectionContent$, subsections$]).pipe(
          map(([mappedSection, mappedSubsections]) => ({
            ...mappedSection,
            expanded: false,
            subsections: mappedSubsections
          }))
        );
      })
    );
  }

  private mapSectionImages(section: Section): Observable<Section> {
    return forkJoin(
      section.sectionContent.map((content: any) => {
        if (content.image) {
          content.image = this.mapHygraphImages(content.image);
        }

        if (!content.techInsightsImages || content.techInsightsImages.length === 0) {
          return of(content);
        }

        const { techInsightsImages, ...rest } = content;
        return forkJoin(techInsightsImages.map(
          (image: any) => this.thumbnailService
            .getRawThumbnailUrlForObjectId(image.cl_object_id, this.THUMBNAIL_HEIGHT, this.THUMBANAIL_WIDTH)
            .pipe(map(response => ({
              fileName: image.filename,
              caption: image.caption,
              url: response,
              sourceUrl: image.assetGroupId ?
                `/search/analysis-view/${image.reportCode}?q=${image.assetGroupId}&page=1&size=5000#activeTab=images&imgId=${image.cl_object_id}` :
                `/search/analysis-view/${image.reportCode}?page=1&size=5000#activeTab=images&imgId=${image.cl_object_id}`,
              type: "platform"
            } as PlatformImage)),
              catchError(() => of(null))
            ))).pipe(map(techInsightImages => ({
              ...rest,
              image: [...rest.image, ...(techInsightImages as any[]).filter(image => image !== null)]
            } as SectionContent)))
      })
    ).pipe(
      map(content => ({
        ...section,
        sectionContent: content
      }))
    );
  }

  private mapHygraphImages(images: any[]): HygraphImage[] {
    return images.map(image => ({
      fileName: image.fileName,
      caption: image.caption,
      url: image.url,
      sourceUrl: null,
      type: "hygraph"
    }))
  }

  private mapHygraphCategoryTags(hygraphTags: HygraphCategoryTag[]): CategoryTag[] {
    return hygraphTags.map(tag => ({
      key: tag.key,
      name: tag.displayName,
      type: tag.categoryType
    }));
  }

  private generateRelatedContentLink(relatedContentType: string, relatedContent: any) {
    switch (relatedContentType) {
      case 'analysis':
        return this.router.serializeUrl(
          this.router.createUrlTree(['search', 'analysis-view', relatedContent.analysisCode])
        );
      case 'post':
        return this.router.serializeUrl(
          this.router.createUrlTree(['search', 'blog-viewer', relatedContent.blogId])
        );
      case 'video':
        return this.router.serializeUrl(
          this.router.createUrlTree(['search', 'video-viewer', relatedContent.videoId])
        );
      default:
        return this.router.serializeUrl(
          this.router.parseUrl(relatedContent.contentLink ?? '')
        );
    }
  }
}

/**
 * /contents/{id} model response
 */
interface ContentsResponse {
  reportCode: string;
  reportTitle: string;
  content: Report;
  contentId: string;
  name: string;
  id: string;
  assetDetails: AssetDetails[]
}

/**
 * /asset_content/{id} model response
 */
type AssetContentResponse = ContentsResponse & { downloadEntitled: boolean }


// support/composite interfaces

interface Report {
  id: string;
  title: string;
  templateType: string;
  includeRelatedContent: boolean;
  includeTableOfContent: boolean;
  updatedAt: string;
  analyses: Analysis[];
  contentBodyBlock: ContentBodyBlock;
  relatedContent: RelatedContent[];
  publishDate: string;
  contentHeaderBlock?: ContentHeaderBlock;
  subTitle: string;
  summary: string;
  authors: Author[];
  customerSegment: string[];
  technologyVertical: string;
  themes: string[];
  featuredImage: FeaturedImage;
  categoryTags: HygraphCategoryTag[];
  categoryTagDefaults: HygraphCategoryTag[];
  ignoreDefaultTags: boolean;
  trackViews: boolean;
  showBanner: boolean;
  contentType: string;
}

interface Author {
  name: string;
}

interface FeaturedImage {
  url: string;
}

interface HygraphCategoryTag {
  categoryType: string;
  key: string;
  displayName: string;
}

interface ContentBodyBlock {
  id: string;
  section: Section[];
}

interface Analysis {
  analysisCode: string;
}

interface DownloadEnabled {
  pdf: boolean;
  image: boolean;
  pcb_image: boolean;
  data_grid: boolean;
  circuit_vision: boolean;
  quicksight: boolean;
}
